import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, combineLatest, map, of, switchMap, firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { 
  SalesPartner, 
  SalesPartnerSale, 
  SalesPartnerCommission, 
  SalesPartnerGoal,
  SalesPartnerDashboardStats 
} from '../interfaces/sales-partner.interface';
import { AuthService } from './auth.service';
import { User } from '../interfaces/user.interface';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SalesPartnerService {

  constructor(
    private firestore: AngularFirestore,
    private authService: AuthService
  ) {}

  // Check if current user is a sales partner
  isCurrentUserSalesPartner(): Observable<boolean> {
    return this.authService.user$.pipe(
      map((user: any) => user?.isSalesPartner === true)
    );
  }

  // Get current user's sales partner profile
  getCurrentUserSalesPartner(): Observable<SalesPartner | null> {
    return this.authService.user$.pipe(
      switchMap((user: any) => {
        if (!user?.salesPartnerId) {
          return of(null);
        }
        return this.firestore
          .collection<SalesPartner>('salesPartners')
          .doc(user.salesPartnerId)
          .valueChanges({ idField: 'id' })
          .pipe(
            map(partner => partner || null)
          );
      })
    );
  }

  // Create a new sales partner
  async createSalesPartner(user: any, commissionRate: number = 10): Promise<string> {
    const referralCode = this.generateReferralCode(user.firstName, user.lastName);
    const referralLink = this.generateReferralLink(referralCode);

    const salesPartner: Omit<SalesPartner, 'id'> = {
      userId: user.uid || user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      referralCode,
      referralLink,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      commissionRate, // Use provided commission rate
      commissionType: 'percentage',
      totalSales: 0,
      totalCommission: 0,
      totalCustomers: 0,
      notifications: {
        emailOnNewSale: true,
        emailOnCommissionUpdate: true,
        emailOnGoalReached: true
      }
    };

    // Create sales partner document
    const salesPartnerRef = await this.firestore
      .collection('salesPartners')
      .add(salesPartner);

    // Update user document to mark as sales partner
    await this.firestore
      .collection('users')
      .doc(user.uid || user.id)
      .update({
        isSalesPartner: true,
        salesPartnerId: salesPartnerRef.id,
        updatedAt: new Date()
      });

    return salesPartnerRef.id;
  }

  // Generate unique referral code
  private generateReferralCode(firstName: string, lastName: string): string {
    const namePrefix = (firstName.substring(0, 2) + lastName.substring(0, 2)).toUpperCase();
    const timestamp = Date.now().toString(36).slice(-4).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${namePrefix}${timestamp}${random}`;
  }

  // Get sales partner by referral code
  getSalesPartnerByReferralCode(referralCode: string): Observable<SalesPartner | null> {
    return this.firestore
      .collection<SalesPartner>('salesPartners', ref => 
        ref.where('referralCode', '==', referralCode).where('isActive', '==', true).limit(1)
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(partners => partners.length > 0 ? partners[0] : null)
      );
  }

  // Get sales partner dashboard stats
  getSalesPartnerDashboardStats(salesPartnerId: string): Observable<SalesPartnerDashboardStats> {
    return combineLatest([
      this.getSalesPartnerSales(salesPartnerId),
      this.getSalesPartnerCommissions(salesPartnerId),
      this.getParentCommissions(salesPartnerId),
      this.getSalesPartnerGoals(salesPartnerId)
    ]).pipe(
      map(([sales, commissions, parentCommissions, goals]) => {
        const now = new Date();
        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        
        const thisMonthSales = sales.filter(sale => 
          sale.saleDate?.toDate() >= thisMonth
        );

        // 🔧 SIMPLE FIX: Calculate commission stats directly from commission records
        const paidCommissions = commissions.filter(c => c.status === 'paid');
        const pendingCommissions = commissions.filter(c => c.status === 'pending');
        const approvedCommissions = commissions.filter(c => c.status === 'approved');
        const requestedCommissions = commissions.filter(c => c.status === 'requested');
        
        // Calculate simple totals by status (direct commissions)
        const totalCommissionFromRecords = commissions.reduce((sum, c) => sum + c.amount, 0);
        const pendingCommissionTotal = pendingCommissions.reduce((sum, c) => sum + c.amount, 0);
        const approvedCommissionTotal = approvedCommissions.reduce((sum, c) => sum + c.amount, 0);
        const requestedCommissionTotal = requestedCommissions.reduce((sum, c) => sum + c.amount, 0);
        const paidCommissionTotal = paidCommissions.reduce((sum, c) => sum + c.amount, 0);
        
        // Calculate parent commission totals
        const parentCommissionsTotal = parentCommissions.reduce((sum, c) => sum + c.amount, 0);
        const parentCommissionsPaid = parentCommissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.amount, 0);
        const parentCommissionsApproved = parentCommissions.filter(c => c.status === 'approved').reduce((sum, c) => sum + c.amount, 0);
        const parentCommissionsPending = parentCommissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.amount, 0);
        const parentCommissionsRequested = parentCommissions.filter(c => c.status === 'requested').reduce((sum, c) => sum + c.amount, 0);
        
        // 🔧 FIX: Calculate total revenue using netOrderAmount field
        const totalRevenue = sales.reduce((sum, sale) => {
          // Use netOrderAmount if available, otherwise fallback to orderAmount calculation
          if (sale.netOrderAmount) {
            return sum + sale.netOrderAmount;
          } else if (sale.taxAmount && sale.taxCalculationMethod?.includes('already_net')) {
            // Fallback: This is a trial subscription with NET orderAmount - use as-is
            return sum + sale.orderAmount;
          } else if (sale.taxAmount) {
            // Fallback: Regular sales - orderAmount is GROSS, subtract tax to get NET
            return sum + (sale.orderAmount - sale.taxAmount);
          } else {
            // Fallback: No tax info available - assume orderAmount is NET or treat as NET
            return sum + sale.orderAmount;
          }
        }, 0);
        const uniqueCustomers = new Set(sales.map(sale => sale.customerEmail)).size;

        // Calculate subscription-aware commission totals
        const subscriptionCommissionTotals = this.calculateSubscriptionAwareCommissions(sales, commissions);
        const thisMonthSubscriptionCommissions = this.calculateSubscriptionAwareCommissions(thisMonthSales, commissions);

        // Calculate total commission from sales (includes future expected commissions)
        const totalCommissionFromSales = sales.reduce((sum, sale) => sum + sale.commissionAmount, 0);
        
        // Calculate pending as difference: Total - (Approved + Requested + Paid)
        const processedCommissions = approvedCommissionTotal + requestedCommissionTotal + paidCommissionTotal;
        const pendingCommissionCalculated = Math.max(0, totalCommissionFromSales - processedCommissions);

        return {
          totalSales: sales.length,
          totalRevenue: totalRevenue,
          totalCommission: totalCommissionFromSales + parentCommissionsTotal, // Include parent commissions
          totalCustomers: uniqueCustomers,
          pendingCommission: pendingCommissionCalculated + parentCommissionsPending, // Include parent pending
          approvedCommission: approvedCommissionTotal + parentCommissionsApproved, // Include parent approved
          requestedCommission: requestedCommissionTotal + parentCommissionsRequested, // Include parent requested
          paidCommission: paidCommissionTotal + parentCommissionsPaid, // Include parent paid
          thisMonthSales: thisMonthSales.length,
          thisMonthCommission: thisMonthSubscriptionCommissions.totalCommission,
          conversionRate: 0, // TODO: Calculate based on tracked visits
          avgOrderValue: sales.length > 0 ? totalRevenue / sales.length : 0,
          activeGoals: goals.filter(g => g.isActive && !g.isCompleted),
          completedGoals: goals.filter(g => g.isCompleted),
          recentSales: sales.map(sale => ({
            ...sale,
            // 🚨 CRITICAL FIX: Always use sale.commissionAmount as-is (no multiplication)
            // The sale.commissionAmount is already the correct total amount
            commissionAmount: sale.commissionAmount
          })), // Show all sales with correct commission amounts
          recentCommissions: [...commissions, ...parentCommissions] // Show all commissions including parent
        };
      })
    );
  }

  // Helper method to calculate subscription-aware commission statistics
  private calculateSubscriptionAwareCommissions(sales: SalesPartnerSale[], allCommissions: SalesPartnerCommission[]): {
    totalCommission: number;
    pendingCommission: number;
    approvedCommission: number;
    requestedCommission: number;
    paidCommission: number;
  } {
    let totalCommission = 0;
    let pendingCommission = 0;
    let approvedCommission = 0;
    let requestedCommission = 0;
    let paidCommission = 0;

    // Group commissions by sale/customer for subscription handling
    const commissionsBySale = new Map<string, SalesPartnerCommission[]>();
    allCommissions.forEach(commission => {
      if (!commissionsBySale.has(commission.saleId)) {
        commissionsBySale.set(commission.saleId, []);
      }
      commissionsBySale.get(commission.saleId)!.push(commission);
    });

    sales.forEach(sale => {
      // Try to find commissions by sale.id first, then by stripeSessionId
      let saleCommissions = commissionsBySale.get(sale.id) || [];
      if (saleCommissions.length === 0 && sale.stripeSessionId) {
        saleCommissions = commissionsBySale.get(sale.stripeSessionId) || [];
      }
      
      // 🔧 SPECIAL CASE: Handle Stripe session ID mismatch
      // Check if any commission has matching customerEmail for this sale
      if (saleCommissions.length === 0) {
        saleCommissions = allCommissions.filter(c => c.customerEmail === sale.customerEmail);
      }
      
      const subscriptionDuration = sale.subscriptionDuration || sale.installmentDuration;
      
      if (sale.isSubscription) {
        // 🚨 CRITICAL FIX: Always use the sale.commissionAmount for subscriptions
        // Commission records might be installment fragments while sale has the correct total
        totalCommission += sale.commissionAmount;

        // Calculate status-based amounts for subscriptions
        const paidCount = saleCommissions.filter((c: SalesPartnerCommission) => c.status === 'paid').length;
        const approvedCount = saleCommissions.filter((c: SalesPartnerCommission) => c.status === 'approved').length;
        const requestedCount = saleCommissions.filter((c: SalesPartnerCommission) => c.status === 'requested').length;
        const pendingCount = saleCommissions.filter((c: SalesPartnerCommission) => c.status === 'pending').length;

        // 🚨 CRITICAL FIX: For status-based amounts, always use sale.commissionAmount
        // Commission records are installment fragments, we need the full sale amount per status
        if (saleCommissions.length > 0) {
          // Use the sale commission amount based on the OVERALL status of commission records
          const hasPaid = saleCommissions.some(c => c.status === 'paid');
          const hasApproved = saleCommissions.some(c => c.status === 'approved');
          const hasRequested = saleCommissions.some(c => c.status === 'requested');
          const hasPending = saleCommissions.some(c => c.status === 'pending');
          
          // Assign the full sale commission amount to the highest priority status
          if (hasPaid) {
            paidCommission += sale.commissionAmount;
          } else if (hasApproved) {
            approvedCommission += sale.commissionAmount;  
          } else if (hasRequested) {
            requestedCommission += sale.commissionAmount;
          } else if (hasPending) {
            pendingCommission += sale.commissionAmount;
          }
        } else {
          // No commission records exist yet - all pending
          pendingCommission += sale.commissionAmount;
        }
      } else {
        // For one-time purchases: Use original logic
        const saleCommission = saleCommissions[0];
        if (saleCommission) {
          totalCommission += saleCommission.amount;
          
          switch (saleCommission.status) {
            case 'paid':
              paidCommission += saleCommission.amount;
              break;
            case 'approved':
              approvedCommission += saleCommission.amount;
              break;
            case 'requested':
              requestedCommission += saleCommission.amount;
              break;
            case 'pending':
              pendingCommission += saleCommission.amount;
              break;
          }
        }
      }
    });

    return {
      totalCommission: Math.round(totalCommission * 100) / 100,
      pendingCommission: Math.round(pendingCommission * 100) / 100,
      approvedCommission: Math.round(approvedCommission * 100) / 100,
      requestedCommission: Math.round(requestedCommission * 100) / 100,
      paidCommission: Math.round(paidCommission * 100) / 100
    };
  }

  // Get sales for a sales partner
  getSalesPartnerSales(salesPartnerId: string): Observable<SalesPartnerSale[]> {
    return this.firestore
      .collection<SalesPartnerSale>('salesPartnerSales', ref =>
        ref.where('salesPartnerId', '==', salesPartnerId)
           .orderBy('saleDate', 'desc')
      )
      .valueChanges({ idField: 'id' });
  }

  // Get commissions for a sales partner (direct commissions only)
  getSalesPartnerCommissions(salesPartnerId: string): Observable<SalesPartnerCommission[]> {
    // Load all commissions for this sales partner, then filter in code
    // This ensures we also get old commissions that don't have isParentCommission field
    return this.firestore
      .collection<SalesPartnerCommission>('salesPartnerCommissions', ref =>
        ref.where('salesPartnerId', '==', salesPartnerId)
           .orderBy('createdAt', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        map(commissions => {
          // Filter out parent commissions (isParentCommission === true)
          // Include commissions where isParentCommission is false, undefined, or null
          return commissions.filter(c => c.isParentCommission !== true);
        }),
        catchError(() => {
          // Fallback: try without orderBy if index doesn't exist
          return this.firestore
            .collection<SalesPartnerCommission>('salesPartnerCommissions', ref =>
              ref.where('salesPartnerId', '==', salesPartnerId)
            )
            .valueChanges({ idField: 'id' })
            .pipe(
              map(commissions => {
                // Filter out parent commissions and sort manually
                const filtered = commissions.filter(c => c.isParentCommission !== true);
                return filtered.sort((a, b) => {
                  const dateA = a.createdAt?.toDate?.() || new Date(0);
                  const dateB = b.createdAt?.toDate?.() || new Date(0);
                  return dateB.getTime() - dateA.getTime(); // Descending
                });
              })
            );
        })
      );
  }

  // Get parent commissions for a sales partner
  getParentCommissions(salesPartnerId: string): Observable<SalesPartnerCommission[]> {
    return this.firestore
      .collection<SalesPartnerCommission>('salesPartnerCommissions', ref =>
        ref.where('parentSalesPartnerId', '==', salesPartnerId)
           .where('isParentCommission', '==', true)
           .orderBy('createdAt', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .pipe(
        catchError(() => {
          // Fallback if index doesn't exist yet
          return this.firestore
            .collection<SalesPartnerCommission>('salesPartnerCommissions', ref =>
              ref.where('parentSalesPartnerId', '==', salesPartnerId)
                 .orderBy('createdAt', 'desc')
            )
            .valueChanges({ idField: 'id' })
            .pipe(
              map(commissions => commissions.filter(c => c.isParentCommission === true))
            );
        })
      );
  }

  // Get goals for a sales partner
  getSalesPartnerGoals(salesPartnerId: string): Observable<SalesPartnerGoal[]> {
    return this.firestore
      .collection<SalesPartnerGoal>('salesPartnerGoals', ref =>
        ref.where('salesPartnerId', '==', salesPartnerId)
           .orderBy('createdAt', 'desc')
      )
      .valueChanges({ idField: 'id' });
  }

  // Record a new sale for a sales partner
  async recordSale(
    salesPartnerId: string,
    referralCode: string,
    customerId: string,
    customerEmail: string,
    customerName: string,
    productId: string,
    productName: string,
    orderAmount: number,
    commissionRate: number,
    stripeSessionId?: string
  ): Promise<void> {
    const commissionAmount = (orderAmount * commissionRate) / 100;

    const sale: Omit<SalesPartnerSale, 'id'> = {
      salesPartnerId,
      referralCode,
      customerId,
      customerEmail,
      customerName,
      productId,
      productName,
      orderAmount,
      commissionAmount,
      commissionRate,
      stripeSessionId,
      saleDate: new Date(),
      paymentStatus: 'completed',
      commissionStatus: 'pending',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Create sale record
    const saleRef = await this.firestore
      .collection('salesPartnerSales')
      .add(sale);

    // Create commission record
    const commission: Omit<SalesPartnerCommission, 'id'> = {
      salesPartnerId,
      saleId: saleRef.id,
      amount: commissionAmount,
      currency: 'EUR', // TODO: Make configurable
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await this.firestore
      .collection('salesPartnerCommissions')
      .add(commission);

    // Update sales partner stats
    await this.updateSalesPartnerStats(salesPartnerId, orderAmount, commissionAmount);
  }

  // Update sales partner statistics
  private async updateSalesPartnerStats(
    salesPartnerId: string,
    orderAmount: number,
    commissionAmount: number
  ): Promise<void> {
    const salesPartnerRef = this.firestore
      .collection('salesPartners')
      .doc(salesPartnerId);

    await this.firestore.firestore.runTransaction(async (transaction) => {
      const salesPartnerDoc = await transaction.get(salesPartnerRef.ref);
      
      if (salesPartnerDoc.exists) {
        const currentData = salesPartnerDoc.data() as SalesPartner;
        
        transaction.update(salesPartnerRef.ref, {
          totalSales: (currentData.totalSales || 0) + 1,
          totalCommission: (currentData.totalCommission || 0) + commissionAmount,
          totalCustomers: (currentData.totalCustomers || 0) + 1, // TODO: Check if customer is new
          updatedAt: new Date()
        });
      }
    });
  }

  // Update sales partner settings
  async updateSalesPartnerSettings(
    salesPartnerId: string,
    updates: Partial<SalesPartner>
  ): Promise<void> {
    await this.firestore
      .collection('salesPartners')
      .doc(salesPartnerId)
      .update({
        ...updates,
        updatedAt: new Date()
      });
  }

  // Generate environment-specific referral link
  private generateReferralLink(referralCode: string): string {
    return `${environment.frontendUrl}/shop?ref=${referralCode}`;
  }

  // Request payout for commissions
  async requestPayout(commissionIds: string[]): Promise<{ success: boolean; message: string; totalAmount?: number }> {
    try {
      const user = await firstValueFrom(this.authService.user$);
      
      if (!user?.salesPartnerId) {
        return {
          success: false,
          message: 'Benutzer ist kein Vertriebspartner'
        };
      }

      const idToken = await this.authService.getIdToken();
      const url = `${environment.apiUrl}/api/sales-partner/request-payout`;
      
      console.log('Making payout request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          salesPartnerId: user.salesPartnerId,
          commissionIds,
          requestedByUserId: user.uid
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        console.error('HTTP error response:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        return {
          success: false,
          message: `HTTP ${response.status}: ${response.statusText}`
        };
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Payout request failed:', error);
      return {
        success: false,
        message: 'Fehler beim Beantragen der Auszahlung: ' + (error instanceof Error ? error.message : String(error))
      };
    }
  }

  // Get approved commissions for payout request
  getApprovedCommissions(salesPartnerId: string): Observable<SalesPartnerCommission[]> {
    return this.firestore
      .collection<SalesPartnerCommission>('salesPartnerCommissions', ref =>
        ref.where('salesPartnerId', '==', salesPartnerId)
           .where('status', '==', 'approved')
           .orderBy('createdAt', 'desc')
      )
      .valueChanges({ idField: 'id' });
  }

  // Get eligible commissions for payout (minimum €50 threshold)
  getEligibleCommissionsForPayout(salesPartnerId: string): Observable<{ commissions: SalesPartnerCommission[]; totalAmount: number; meetsThreshold: boolean }> {
    return this.getApprovedCommissions(salesPartnerId).pipe(
      map(commissions => {
        const totalAmount = commissions.reduce((sum, c) => sum + c.amount, 0);
        const meetsThreshold = totalAmount >= 50; // €50 minimum threshold
        
        return {
          commissions,
          totalAmount,
          meetsThreshold
        };
      })
    );
  }

  // Update tax information for current user's sales partner
  async updateTaxInformation(taxInfo: {
    isKleinunternehmer: boolean;
    taxId: string;
    address: {
      street: string;
      city: string;
      postalCode: string;
      country: string;
    };
  }): Promise<void> {
    const user = await firstValueFrom(this.authService.user$);
    if (!user?.salesPartnerId) {
      throw new Error('Benutzer ist kein Vertriebspartner');
    }

    await this.firestore
      .collection('salesPartners')
      .doc(user.salesPartnerId)
      .update({
        isKleinunternehmer: taxInfo.isKleinunternehmer,
        taxId: taxInfo.taxId,
        address: taxInfo.address,
        updatedAt: new Date()
      });
  }

  // Deactivate sales partner
  async deactivateSalesPartner(salesPartnerId: string): Promise<void> {
    await this.firestore
      .collection('salesPartners')
      .doc(salesPartnerId)
      .update({
        isActive: false,
        updatedAt: new Date()
      });
  }
}